import {
  feedbackReplySchema,
  type FeedbackDevice,
  type FeedbackReply,
  type FeedbackRequest,
} from '../../shared/feedback';
import { API_PATHS } from '../../shared/routes';

const FEEDBACK_TIMEOUT_MS = 25_000;

/** Coarse, opt-in context only. Never send the raw UA, URLs, storage or room state. */
export function feedbackDevice(
  userAgent: string,
  touchPoints: number,
  width: number,
  height: number,
): FeedbackDevice {
  const match = /(?:Edg|EdgiOS|EdgA)\/(\d+)/.exec(userAgent);
  const firefox = /(?:Firefox|FxiOS)\/(\d+)/.exec(userAgent);
  const chrome = /(?:Chrome|CriOS)\/(\d+)/.exec(userAgent);
  const safari = /Version\/(\d+).*Safari\//.exec(userAgent);
  const major = Number((match ?? firefox ?? chrome ?? safari)?.[1]);
  return {
    browser: match ? 'Edge' : firefox ? 'Firefox' : chrome ? 'Chrome' : safari ? 'Safari' : 'Other',
    browserMajor: Number.isInteger(major) && major > 0 && major <= 9999 ? major : null,
    platform:
      /iPhone|iPad|iPod/.test(userAgent) || (/Macintosh/.test(userAgent) && touchPoints > 1)
        ? 'iOS'
        : /Android/.test(userAgent)
          ? 'Android'
          : /Macintosh|Mac OS X/.test(userAgent)
            ? 'macOS'
            : /Windows/.test(userAgent)
              ? 'Windows'
              : /Linux/.test(userAgent)
                ? 'Linux'
                : 'Other',
    width: Math.max(1, Math.min(10000, Math.round(width) || 1)),
    height: Math.max(1, Math.min(10000, Math.round(height) || 1)),
  };
}

/** Network/unknown failures are ambiguous: callers must keep the same submission ID. */
export async function sendFeedback(
  input: FeedbackRequest,
  credential: string,
): Promise<FeedbackReply | { status: 'unknown' }> {
  try {
    // Not `fetchJson`: a non-2xx reply still carries a typed status the caller must read.
    const response = await fetch(API_PATHS.feedback, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${credential}` },
      body: JSON.stringify(input),
      signal: AbortSignal.timeout(FEEDBACK_TIMEOUT_MS),
    });
    const parsed = feedbackReplySchema.safeParse(await response.json());
    if (!parsed.success || (parsed.data.status === 'created' && !response.ok)) return { status: 'unknown' };
    return parsed.data;
  } catch {
    return { status: 'unknown' };
  }
}
